import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { ERROR_TYPE, getAllErrors, renderErrorMessages, useSearch } from "@/utils";

import { Button } from "@/components/Button/Button";

import { InputField } from "@/components/InputField/InputField";

import { ButtonColor } from "@/components/Button/types";

import { SelectField, SelectFieldValue } from "@/components/SelectField/SelectField";

import { ScrapeLevel } from "@/enum/job.enum";
import { ParallelAiMonitorFrequency, ParallelAiType } from "@/enum/parallel-ai-type.enum";

import { useLazyGetFilterByPropertyQuery } from "@/pages/Private/redux/list/list.api";

import {
	useUpdateParallelAiMutation,
	useCreateParallelAiMutation,
} from "@/pages/Private/redux/parallelAi/parallelAi.api";

import { TextArea } from "@/components/TextArea/TextArea";

import { resolveSourceMode } from "@/pages/Private/pages/List/pages/ListCreate/utils/sourceMode";

import { List } from "../../List/schema/list";
import { CreateParallelAi, CreateParallelAiSchema, ParallelAi } from "../schema/parallelAi";
import { ParallelAiModeSelector } from "./ParallelAiModeSelector";
import { normalizePayloadForMode } from "./parallelAiPayload";

interface CreateParallelAiFormProps {
	parallelAi?: ParallelAi | null;
	list: List;
	handleSelect?: (parallelAi: ParallelAi | null) => void;
	handleDuplicate?: (parallelAi: ParallelAi | null) => void;
}

export const CreateParallelAiForm: FunctionComponent<CreateParallelAiFormProps> = ({
	parallelAi,
	list,
	handleSelect,
	handleDuplicate,
}) => {
	const initialState = {
		name: "",
		query: "",
		type: ParallelAiType.SEARCH,
		frequency: ParallelAiMonitorFrequency.DAILY,
		scrapeType: ScrapeLevel.GET_ENRICHMENT,
		inTitle: false,
		repeatDaily: false,
	};

	const { t } = useTranslation();
	const ts = (key: string) => t(`parallelAi.${key}`);

	const [mode, setMode] = useState<ParallelAiType>(ParallelAiType.SEARCH);

	const [options, setOptions] = useState<
		{
			title: string;
			id: string;
		}[]
	>([]);

	const [update] = useUpdateParallelAiMutation();
	const [createParallelAi, { error, isLoading }] = useCreateParallelAiMutation();

	const { query } = useSearch();

	const [currentFormState, setCurrentFormState] = useState<Partial<CreateParallelAi>>(initialState);
	const [getFilterByValue] = useLazyGetFilterByPropertyQuery();

	useEffect(() => {
		if (query) {
			const updateOptions = async (query: string) => {
				const result = await getFilterByValue({
					propertyName: "title",
					searchTerm: query,
				}).unwrap();

				setOptions(result?.data?.map((r) => ({ title: r.value, id: r.value })) || []);
			};

			updateOptions(query);
		}
	}, [getFilterByValue, query]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSaveFormState = (value: any, name: keyof CreateParallelAi) => {
		const newValue = {
			...currentFormState,
			[name]: value,
		};

		setCurrentFormState(newValue);

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const {
		reset,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<CreateParallelAi>({
		defaultValues: initialState,
		resolver: zodResolver(CreateParallelAiSchema),
	});

	const handleModeChange = (nextMode: ParallelAiType) => {
		setMode(nextMode);
		handleSaveFormState(nextMode, "type");
	};

	useEffect(() => {
		if (parallelAi) {
			const nextMode = parallelAi.type ?? ParallelAiType.SEARCH;
			const formState = {
				name: parallelAi.name,
				query: parallelAi.query,
				type: nextMode,
				frequency: parallelAi.frequency ?? ParallelAiMonitorFrequency.DAILY,
				listId: list.id,
			};

			setMode(nextMode);

			if (handleDuplicate) {
				reset({
					...formState,
				});
				setCurrentFormState({
					...formState,
				});
			} else {
				reset({
					id: parallelAi.id,
					...formState,
				});
				setCurrentFormState({
					...formState,
					id: parallelAi?.id,
				});
			}
		} else {
			const initialMode = resolveSourceMode(Object.values(ParallelAiType), ParallelAiType.SEARCH);

			setMode(initialMode);
			reset({ ...initialState, type: initialMode });
		}
	}, [parallelAi]);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: CreateParallelAi) => {
		try {
			const payload = normalizePayloadForMode(values, mode);

			if (payload.id && !handleDuplicate) {
				await update({
					...payload,
					id: payload.id,
					listId: list.id,
				}).unwrap();
			} else {
				await createParallelAi({
					...payload,
					listId: list.id,
				}).unwrap();
			}

			handleSelect?.(null);
			handleDuplicate?.(null);

			reset();
		} catch (err) {
			console.error(err);
		}
	};
	const buttonTitle =
		handleDuplicate && parallelAi?.id
			? t("basics.copyCriteria")
			: parallelAi?.id
				? t("basics.save")
				: t("basics.addCriteria");

	const isMonitor = mode === ParallelAiType.MONITOR;

	return (
		<>
			<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
				<div className="text-md mb-5">{ts("addSource")}</div>

				<div className="mb-5">
					<ParallelAiModeSelector mode={mode} onChange={handleModeChange} />
				</div>

				<div className="flex flex-col gap-4 w-full mb-4">
					<InputField
						error={!!errors.name?.message}
						handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
							handleSaveFormState(event.target.value, "name");
						}}
						label={ts("sourceNameLabel")}
						name={"name"}
						placeholder="DE - China Companies"
						value={currentFormState.name ?? ""}
					/>
					<div>
						<TextArea
							className="!min-h-[120px]"
							error={!!errors.query?.message}
							handleChange={function (event: ChangeEvent<HTMLTextAreaElement>): void {
								handleSaveFormState(event.target.value, "query");
							}}
							label={isMonitor ? ts("monitorQueryLabel") : ts("searchQueryLabel")}
							name={"query"}
							placeholder={
								isMonitor ? ts("monitorQueryPlaceholder") : "e.g. DE companies with ties to China"
							}
							showError={false}
							value={currentFormState.query ?? ""}
						/>
					</div>

					{isMonitor && (
						<>
							<SelectField
								hideEmptyValue
								handleSelect={(value: SelectFieldValue) => handleSaveFormState(value, "frequency")}
								label={ts("frequencyLabel")}
								options={[
									{ title: ts("frequencyDaily"), value: ParallelAiMonitorFrequency.DAILY },
									{ title: ts("frequencyWeekly"), value: ParallelAiMonitorFrequency.WEEKLY },
								]}
								selectedOption={currentFormState.frequency ?? ParallelAiMonitorFrequency.DAILY}
								testId="parallel-ai-frequency"
							/>
							<p className="text-sm text-text-meta" data-testid="parallel-ai-monitor-hint">
								{ts("monitorOngoingHint")}
							</p>
						</>
					)}
				</div>

				{getAllErrors(error, formErrors).length ? (
					<div className="mb-4">{renderErrorMessages(getAllErrors(error, formErrors))}</div>
				) : null}

				<div className="flex gap-4 w-full justify-end">
					{handleSelect && (
						<div className="flex max-w-[100px] w-full">
							<Button
								className="w-full"
								color={ButtonColor.ACTION_SECONDARY}
								title="cancel"
								onClick={() => {
									handleSelect(null);
									handleDuplicate?.(null);
									setCurrentFormState(initialState);
									setMode(ParallelAiType.SEARCH);
									reset(initialState);
								}}
							/>
						</div>
					)}

					<div className="flex max-w-[150px] w-full">
						<Button
							className="w-full"
							isLoading={isLoading}
							title={buttonTitle}
							onClick={handleSubmit(onSubmit)}
						/>
					</div>
				</div>
			</div>
		</>
	);
};
